import { InlineError } from "@/modules/form-field/errors";
import { useField$Custom } from "@/modules/form-field/hooks/useField$Custom";
import { fromNatural, toNatural } from "@/modules/form-field/utils/conversions";

type Value = number;

type Draft = string;

type Params = {
  initialValue: Value | undefined;
  min: Value;
  max: Value;
};

function normalizeBeforeValidate(draft: Draft) {
  // preprocessing: remove all spaces, and commas (thousand separator)
  return draft.replace(/[\s,]/g, "");
}

function validateDraft(draft: Draft, params: Params) {
  const value = toNatural(draft);
  InlineError.assert$PunishAfterEditing(
    value >= params.min,
    `Cannot be less than ${params.min} editions`
  );
  InlineError.assert$PunishAfterEditing(
    value <= params.max,
    `Cannot be more than ${params.max} editions`
  );
}

const useFieldNumEditions$CoreApi = useField$Custom.withConfig<
  Value,
  Draft,
  Params
>({
  getInitialDraft: (params: Params) =>
    params.initialValue != null ? fromNatural(params.initialValue) : "",
  draftToValue: toNatural,
  valueToDraft: fromNatural,
  normalizeBeforeValidate,
  validateDraft,
});

export function useFieldNumEditions(params: Params) {
  const api = useFieldNumEditions$CoreApi(params);

  const currentValue = api.value;

  const increase = !api.draft.trim()
    ? () => api.setValue(1)
    : currentValue != null && currentValue < params.max
    ? () => api.setValue(currentValue + 1)
    : undefined;

  const decrease =
    currentValue != null && currentValue > params.min
      ? () => api.setValue(currentValue - 1)
      : undefined;

  const setToMax = () => api.setValue(params.max);

  return { ...api, increase, decrease, setToMax };
}
